import {
	Box,
	Button,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableRow,
	TableHead,
	TableContainer,
} from "@mui/material";
import { useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import { GroupedGeoJSON } from "../../../utilities/types";
import { FeatureCollection } from "geojson";

interface FolderLevelsTableProps {
	data: GroupedGeoJSON[];
	truncate?: number;
	onClear?: (item: GroupedGeoJSON) => void;
}

// Helper function to extract path information from GeoJSON properties
function getPathInfoFromGeoJSON(
	geoJSON: FeatureCollection,
): { filePath: string | null; folderLevels: Record<string, string> } | null {
	if (geoJSON.features && geoJSON.features.length > 0) {
		const properties = geoJSON.features[0].properties;
		if (properties) {
			let filePath: string | null = null;
			const folderLevels: Record<string, string> = {};

			// Extract FilePath
			if (properties.FilePath) {
				filePath = properties.FilePath;
			}

			// Extract FolderLevel properties
			Object.keys(properties).forEach((key) => {
				if (key.startsWith("FolderLevel")) {
					const level = key.replace("FolderLevel", "");
					folderLevels[level] = properties[key];
				}
			});

			if (filePath || Object.keys(folderLevels).length > 0) {
				return { filePath, folderLevels };
			}
		}
	}
	return null;
}

export function FolderLevelsTable(props: FolderLevelsTableProps) {
	const [truncated, setTruncated] = useState<boolean>(true);
	const items = props.data;

	// Get all unique folder levels across all files to determine table columns
	const allFolderLevels = new Set<string>();
	items.forEach((file) => {
		const pathInfo = getPathInfoFromGeoJSON(file.geoJSON);
		if (pathInfo) {
			Object.keys(pathInfo.folderLevels).forEach((level) => allFolderLevels.add(level));
		}
	});
	const sortedLevels = Array.from(allFolderLevels).sort((a, b) => parseInt(a) - parseInt(b));

	const displayFiles = truncated && props.truncate ? items.slice(0, props.truncate) : items;

	return (
		<>
			{sortedLevels.length > 0 ? (
				<>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
						Folder Levels:
					</Typography>
					<TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
						<Table size="small" sx={{ minWidth: "max-content" }}>
							<TableHead>
								<TableRow>
									{!!props.onClear && <TableCell sx={{ fontWeight: "bold", px: 1 }}>Actions</TableCell>}
									{/* <TableCell sx={{ fontWeight: 'bold', px: 1 }}>
									File Name
								</TableCell> */}
									{sortedLevels.map((level) => (
										<TableCell key={level} sx={{ fontWeight: "bold", px: 1, minWidth: "120px", maxWidth: "200px" }}>
											Level {level}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{displayFiles.map((file) => {
									const pathInfo = getPathInfoFromGeoJSON(file.geoJSON);
									return (
										<TableRow key={file.groupKey}>
											{!!props.onClear && (
												<TableCell sx={{ px: 1, py: 0.5 }}>
													<Button onClick={() => props.onClear!(file)} sx={{ minWidth: "auto", p: 0.5 }} size="small">
														<ClearIcon fontSize="small" />
													</Button>
												</TableCell>
											)}
											{sortedLevels.map((level) => (
												<TableCell
													key={level}
													sx={{
														px: 1,
														py: 0.5,
														color: "text.secondary",
														minWidth: "120px",
														maxWidth: "200px",
														wordBreak: "break-word",
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}
												>
													{pathInfo?.folderLevels[level] || "-"}
												</TableCell>
											))}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			) : (
				<>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
						Files:
					</Typography>
					<TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
						<Table size="small" sx={{ minWidth: "max-content" }}>
							<TableHead>
								<TableRow>
									{!!props.onClear && <TableCell sx={{ fontWeight: "bold", px: 1 }}>Actions</TableCell>}
									<TableCell sx={{ fontWeight: "bold", px: 1 }}>File Name</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{displayFiles.map((file) => (
									<TableRow key={file.groupKey}>
										{!!props.onClear && (
											<TableCell sx={{ px: 1, py: 0.5 }}>
												<Button onClick={() => props.onClear!(file)} sx={{ minWidth: "auto", p: 0.5 }} size="small">
													<ClearIcon fontSize="small" />
												</Button>
											</TableCell>
										)}
										<TableCell sx={{ px: 1, py: 0.5, wordBreak: "break-word" }}>
											{file.groupKey.split("/").pop() || file.groupKey}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			<Box sx={{ mt: 2, display: "flex", gap: 1 }}>
				{truncated && props.truncate && moreItemsCount() > 0 && (
					<Button
						variant="text"
						disabled={!items || items.length <= (props.truncate || 0)}
						onClick={() => setTruncated(false)}
					>
						Show {moreItemsCount()} More
					</Button>
				)}

				{!truncated && props.truncate && (
					<Button
						variant="text"
						disabled={!items || items.length <= (props.truncate || 0)}
						onClick={() => setTruncated(true)}
					>
						Show Less
					</Button>
				)}
			</Box>
		</>
	);

	function moreItemsCount() {
		return items && items.length > (props.truncate || 0) ? items.length - (props.truncate || 0) : 0;
	}
}
